/**
 * Book-level character roster + per-chapter arc levers (desktop-v2).
 * Source of truth: `book.metadata.characters`. Chapter `tabData.characters` is a derived name list.
 */

export type CharacterRole = "major" | "minor";

export type ArcLeverCategory =
  | "baseline"
  | "motivation"
  | "behavior"
  | "crucible"
  | "resolution";

export type CharacterArcLever = {
  category: ArcLeverCategory;
  score: number; // 1–5
  evidence: string;
};

export type CharacterChapterAppearance = {
  chapterId: string;
  note?: string;
  levers?: CharacterArcLever[];
};

/** Highlight + text color pair for Narrator Mode speaker cues. */
export type CharacterSpeakerColor = {
  highlight: string;
  text: string;
};

export type BookCharacter = {
  id: string;
  name: string;
  aliases: string[];
  description: string;
  role: CharacterRole;
  firstAppearanceChapterId?: string;
  appearances: CharacterChapterAppearance[];
  /** Narrator Mode speaker highlight colors (pastel by default). */
  color?: CharacterSpeakerColor;
  updatedAt: string;
};

/** Author-removed character with reason — informs future extract / roster versions. */
export type CharacterRemovalRecord = {
  id: string;
  /** Original roster id when removed from an existing character. */
  characterId?: string;
  name: string;
  aliases: string[];
  description?: string;
  role?: CharacterRole;
  reason: string;
  removedAt: string;
};

export type LeverDelta = "up" | "down" | "flat" | "new";

export const ARC_LEVER_CATEGORIES: readonly ArcLeverCategory[] = [
  "baseline",
  "motivation",
  "behavior",
  "crucible",
  "resolution",
] as const;

export const ARC_LEVER_LABELS: Record<ArcLeverCategory, string> = {
  baseline: "Baseline",
  motivation: "Motivation",
  behavior: "Behavior",
  crucible: "The Crucible",
  resolution: "Resolution",
};

export const ARC_LEVER_HINTS: Record<ArcLeverCategory, string> = {
  baseline: "Who they are when they enter this chapter’s arc beat",
  motivation: "What they want / drive",
  behavior: "How they act under pressure",
  crucible: "Peak pressure / test in this chapter",
  resolution: "Where they land by chapter end",
};

export function generateCharacterId(): string {
  return `char-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

export function generateCharacterRemovalId(): string {
  return `char-removal-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function normalizeNameKey(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, " ");
}

const MONTH_NAMES = new Set([
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
]);

const WEEKDAY_NAMES = new Set([
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
]);

/** Single-token scrapes / common false positives from capitalized-word heuristics. */
const SINGLE_TOKEN_BLOCKLIST = new Set([
  "the",
  "a",
  "an",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "class",
  "school",
  "home",
  "work",
  "air",
  "force",
  "week",
  "month",
  "year",
  "day",
  "night",
  "morning",
  "evening",
  "afternoon",
  "today",
  "tomorrow",
  "yesterday",
  "halloween",
  "christmas",
  "thanksgiving",
  "easter",
  "january",
  "february",
  "march",
  "april",
  "may",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december",
]);

/** Multi-word phrases that are almost never person names (org / time / place scrapes). */
const PHRASE_BLOCKLIST = new Set([
  "air force",
  "one week",
  "two weeks",
  "one day",
  "two days",
  "one month",
  "two months",
  "one year",
  "two years",
  "before halloween",
  "after school",
  "high school",
  "middle school",
  "united states",
  "new york",
  "los angeles",
]);

const NAME_PARTICLES = new Set(["the", "de", "van", "von", "la", "le", "du", "of", "bin", "ibn"]);

const ROLE_AFTER_THE = new Set([
  "baker",
  "butcher",
  "captain",
  "doctor",
  "duke",
  "guard",
  "king",
  "mayor",
  "nurse",
  "pilot",
  "priest",
  "professor",
  "queen",
  "scribe",
  "smith",
  "stranger",
  "teacher",
  "waiter",
]);

/**
 * Reject capitalized-word scrapes (times, orgs, articles) while keeping real names.
 * Used after LLM extract and when seeding from legacy chapter tab lines.
 */
export function isPlausibleCharacterName(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed || trimmed.length < 2) return false;

  const key = normalizeNameKey(trimmed);
  if (!key) return false;
  if (SINGLE_TOKEN_BLOCKLIST.has(key)) return false;
  if (MONTH_NAMES.has(key)) return false;
  if (WEEKDAY_NAMES.has(key)) return false;
  if (PHRASE_BLOCKLIST.has(key)) return false;

  const words = key.split(" ").filter(Boolean);
  if (words.length === 0) return false;

  if (
    /^(one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\s+(second|seconds|minute|minutes|hour|hours|day|days|week|weeks|month|months|year|years)$/.test(
      key
    )
  ) {
    return false;
  }

  if (/^(before|after|during|until|since|by|on|at|in|within|throughout)\s+/.test(key)) {
    return false;
  }

  if (words.length === 1) {
    return /^[A-Z]/.test(trimmed);
  }

  if (words[0] === "the" && words.length === 2) {
    return ROLE_AFTER_THE.has(words[1]);
  }

  const surfaceWords = trimmed.split(/\s+/);
  for (let i = 0; i < surfaceWords.length; i++) {
    const surface = surfaceWords[i] ?? "";
    const lower = words[i] ?? "";
    if (NAME_PARTICLES.has(lower)) continue;
    if (!/^[A-Z]/.test(surface)) return false;
  }

  return true;
}

export function filterPlausibleExtractedCharacters<T extends { name?: string }>(items: T[]): T[] {
  return items.filter((item) => {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    return name && isPlausibleCharacterName(name);
  });
}

export function clampLeverScore(score: unknown): number {
  const n = typeof score === "number" ? score : Number(score);
  if (!Number.isFinite(n)) return 3;
  return Math.max(1, Math.min(5, Math.round(n)));
}

export function createBlankLevers(): CharacterArcLever[] {
  return ARC_LEVER_CATEGORIES.map((category) => ({
    category,
    score: 3,
    evidence: "",
  }));
}

export function normalizeLevers(raw: unknown): CharacterArcLever[] {
  const byCategory = new Map<ArcLeverCategory, CharacterArcLever>();
  if (Array.isArray(raw)) {
    for (const item of raw) {
      if (!item || typeof item !== "object") continue;
      const rec = item as Record<string, unknown>;
      const category = normalizeCategory(rec.category);
      if (!category) continue;
      byCategory.set(category, {
        category,
        score: clampLeverScore(rec.score),
        evidence: typeof rec.evidence === "string" ? rec.evidence.trim() : "",
      });
    }
  }
  return ARC_LEVER_CATEGORIES.map(
    (category) =>
      byCategory.get(category) ?? {
        category,
        score: 3,
        evidence: "",
      }
  );
}

function normalizeCategory(value: unknown): ArcLeverCategory | null {
  if (typeof value !== "string") return null;
  const key = value.trim().toLowerCase().replace(/\s+/g, "_").replace(/^the_/, "");
  if (key === "baseline") return "baseline";
  if (key === "motivation") return "motivation";
  if (key === "behavior") return "behavior";
  if (key === "crucible" || key === "the_crucible") return "crucible";
  if (key === "resolution") return "resolution";
  return null;
}

export function normalizeAppearance(raw: unknown): CharacterChapterAppearance | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const chapterId = typeof rec.chapterId === "string" ? rec.chapterId.trim() : "";
  if (!chapterId) return null;
  const note = typeof rec.note === "string" ? rec.note.trim() : undefined;
  const levers = rec.levers != null ? normalizeLevers(rec.levers) : undefined;
  return {
    chapterId,
    ...(note ? { note } : {}),
    ...(levers ? { levers } : {}),
  };
}

export function normalizeBookCharacter(raw: unknown, nowIso = new Date().toISOString()): BookCharacter | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  if (!name) return null;
  const id =
    typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateCharacterId();
  const aliases = Array.isArray(rec.aliases)
    ? rec.aliases
        .filter((a): a is string => typeof a === "string")
        .map((a) => a.trim())
        .filter(Boolean)
    : [];
  const description = typeof rec.description === "string" ? rec.description.trim() : "";
  const role: CharacterRole = rec.role === "major" ? "major" : "minor";
  const firstAppearanceChapterId =
    typeof rec.firstAppearanceChapterId === "string" && rec.firstAppearanceChapterId.trim()
      ? rec.firstAppearanceChapterId.trim()
      : typeof rec.firstAppearance === "string" && rec.firstAppearance.trim()
        ? rec.firstAppearance.trim()
        : undefined;
  const appearances: CharacterChapterAppearance[] = [];
  if (Array.isArray(rec.appearances)) {
    for (const item of rec.appearances) {
      const appearance = normalizeAppearance(item);
      if (appearance) appearances.push(appearance);
    }
  } else if (Array.isArray(rec.mentions)) {
    // Legacy Character shape: mentions → appearances without levers
    for (const item of rec.mentions) {
      if (!item || typeof item !== "object") continue;
      const m = item as Record<string, unknown>;
      const chapterId = typeof m.chapterId === "string" ? m.chapterId.trim() : "";
      if (chapterId) appearances.push({ chapterId });
    }
  }
  const updatedAt =
    typeof rec.updatedAt === "string" && rec.updatedAt.trim() ? rec.updatedAt.trim() : nowIso;

  let color: CharacterSpeakerColor | undefined;
  if (rec.color && typeof rec.color === "object") {
    const c = rec.color as Record<string, unknown>;
    const highlight = typeof c.highlight === "string" ? c.highlight.trim() : "";
    const text = typeof c.text === "string" ? c.text.trim() : "";
    if (highlight && text) color = { highlight, text };
  }

  const character: BookCharacter = {
    id,
    name,
    aliases,
    description,
    role,
    appearances:
      role === "major"
        ? appearances.map((a) => ({
            ...a,
            levers: a.levers ? normalizeLevers(a.levers) : undefined,
          }))
        : appearances.map(({ chapterId, note }) => ({
            chapterId,
            ...(note ? { note } : {}),
          })),
    updatedAt,
  };
  if (firstAppearanceChapterId) character.firstAppearanceChapterId = firstAppearanceChapterId;
  if (color) character.color = color;
  return character;
}

export function normalizeCharacterRoster(raw: unknown): BookCharacter[] {
  if (!Array.isArray(raw)) return [];
  const out: BookCharacter[] = [];
  const seenIds = new Set<string>();
  for (const item of raw) {
    const character = normalizeBookCharacter(item);
    if (!character) continue;
    if (!isPlausibleCharacterName(character.name)) continue;
    if (seenIds.has(character.id)) {
      character.id = generateCharacterId();
    }
    seenIds.add(character.id);
    out.push(character);
  }
  return sortCharactersMajorFirst(out);
}

export function sortCharactersMajorFirst(characters: BookCharacter[]): BookCharacter[] {
  return [...characters].sort((a, b) => {
    if (a.role !== b.role) return a.role === "major" ? -1 : 1;
    return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
  });
}

export function characterNameKeys(character: BookCharacter): string[] {
  const keys = new Set<string>();
  const name = normalizeNameKey(character.name);
  if (name) keys.add(name);
  for (const alias of character.aliases) {
    const key = normalizeNameKey(alias);
    if (key) keys.add(key);
  }
  return Array.from(keys);
}

export function findCharacterByNameOrAlias(
  roster: BookCharacter[],
  name: string,
  aliases: string[] = []
): BookCharacter | undefined {
  const keys = new Set<string>();
  const primary = normalizeNameKey(name);
  if (primary) keys.add(primary);
  for (const alias of aliases) {
    const key = normalizeNameKey(alias);
    if (key) keys.add(key);
  }
  if (keys.size === 0) return undefined;

  const exact = roster.find((character) => characterNameKeys(character).some((k) => keys.has(k)));
  if (exact) return exact;

  // Soft match: "Darnay" ↔ "Darnay Wiggins" when a query token equals a roster name token.
  return roster.find((character) => {
    const rosterTokens = new Set(
      characterNameKeys(character).flatMap((k) => k.split(" ").filter((t) => t.length > 1))
    );
    for (const key of keys) {
      const tokens = key.split(" ").filter((t) => t.length > 1);
      if (tokens.length === 1 && rosterTokens.has(tokens[0]!)) return true;
      if (tokens.length > 1 && tokens.every((t) => rosterTokens.has(t))) return true;
    }
    return false;
  });
}

export type ExtractedCharacterPayload = {
  name: string;
  aliases?: string[];
  description?: string;
  role?: CharacterRole | string;
  note?: string;
  /** Whole-book extraction: chapters where this character appears. */
  chapterIds?: string[];
  levers?: Array<{ category?: string; score?: number; evidence?: string }>;
};

export function mergeExtractedCharactersIntoRoster(params: {
  roster: BookCharacter[];
  extracted: ExtractedCharacterPayload[];
  chapterId: string;
  removals?: CharacterRemovalRecord[];
  nowIso?: string;
}): BookCharacter[] {
  const now = params.nowIso ?? new Date().toISOString();
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    if (!name || !isPlausibleCharacterName(name)) continue;
    const aliases = Array.isArray(item.aliases)
      ? item.aliases
          .filter((a): a is string => typeof a === "string")
          .map((a) => a.trim())
          .filter(Boolean)
      : [];
    if (isCharacterNameRemoved(removals, name, aliases)) continue;
    const role: CharacterRole = item.role === "major" ? "major" : "minor";
    const description = typeof item.description === "string" ? item.description.trim() : "";
    const note = typeof item.note === "string" ? item.note.trim() : undefined;
    const levers = role === "major" ? normalizeLevers(item.levers) : undefined;

    const existing = findCharacterByNameOrAlias(next, name, aliases);
    if (existing) {
      const mergedAliases = Array.from(
        new Set([...existing.aliases, ...aliases.filter((a) => normalizeNameKey(a) !== normalizeNameKey(existing.name))])
      );
      // Prefer longer / fuller name when extract provides one
      const preferName =
        name.length > existing.name.length || (role === "major" && existing.role === "minor") ? name : existing.name;
      const nextRole: CharacterRole =
        existing.role === "major" || role === "major" ? "major" : "minor";
      const appearance: CharacterChapterAppearance = {
        chapterId: params.chapterId,
        ...(note ? { note } : {}),
        ...(nextRole === "major" ? { levers: levers ?? createBlankLevers() } : {}),
      };
      const otherAppearances = existing.appearances.filter((a) => a.chapterId !== params.chapterId);
      const prevAppearance = existing.appearances.find((a) => a.chapterId === params.chapterId);
      if (prevAppearance?.note && !appearance.note) appearance.note = prevAppearance.note;
      if (nextRole === "major" && prevAppearance?.levers && !item.levers) {
        appearance.levers = normalizeLevers(prevAppearance.levers);
      }

      next = next.map((c) =>
        c.id === existing.id
          ? {
              ...c,
              name: preferName,
              aliases: mergedAliases,
              // Prefer lasting book description; only fill when empty. Scene details belong in `note`.
              description: c.description.trim() || description,
              role: nextRole,
              firstAppearanceChapterId: c.firstAppearanceChapterId ?? params.chapterId,
              appearances: [...otherAppearances, appearance],
              updatedAt: now,
            }
          : c
      );
    } else {
      const appearance: CharacterChapterAppearance = {
        chapterId: params.chapterId,
        ...(note ? { note } : {}),
        ...(role === "major" ? { levers: levers ?? createBlankLevers() } : {}),
      };
      next.push({
        id: generateCharacterId(),
        name,
        aliases,
        description,
        role,
        firstAppearanceChapterId: params.chapterId,
        appearances: [appearance],
        updatedAt: now,
      });
    }
  }

  return sortCharactersMajorFirst(next);
}

/**
 * Merge a whole-book cast pass. Unlike chapter extraction, the holistic description is
 * authoritative because it was inferred from the full manuscript rather than one scene.
 * Existing chapter notes and lever scores are preserved.
 */
export function mergeHolisticCharactersIntoRoster(params: {
  roster: BookCharacter[];
  extracted: ExtractedCharacterPayload[];
  validChapterIds: string[];
  removals?: CharacterRemovalRecord[];
  nowIso?: string;
}): BookCharacter[] {
  const now = params.nowIso ?? new Date().toISOString();
  const validIds = new Set(params.validChapterIds);
  const removals = params.removals ?? [];
  let next = [...params.roster];

  for (const item of params.extracted) {
    const name = typeof item.name === "string" ? item.name.trim() : "";
    const aliases = Array.isArray(item.aliases)
      ? item.aliases
          .filter((alias): alias is string => typeof alias === "string")
          .map((alias) => alias.trim())
          .filter(Boolean)
      : [];
    if (
      !name ||
      !isPlausibleCharacterName(name) ||
      isCharacterNameRemoved(removals, name, aliases)
    ) {
      continue;
    }

    const role: CharacterRole = item.role === "major" ? "major" : "minor";
    const description = typeof item.description === "string" ? item.description.trim() : "";
    const chapterIds = Array.from(
      new Set(
        (Array.isArray(item.chapterIds) ? item.chapterIds : [])
          .filter((id): id is string => typeof id === "string")
          .map((id) => id.trim())
          .filter((id) => validIds.has(id))
      )
    );
    const existing = findCharacterByNameOrAlias(next, name, aliases);

    if (existing) {
      const existingByChapter = new Map(existing.appearances.map((appearance) => [
        appearance.chapterId,
        appearance,
      ]));
      const mergedChapterIds = Array.from(
        new Set([...existing.appearances.map((appearance) => appearance.chapterId), ...chapterIds])
      ).filter((id) => validIds.has(id));
      const appearances = mergedChapterIds.map(
        (chapterId) => existingByChapter.get(chapterId) ?? { chapterId }
      );
      const mergedAliases = Array.from(
        new Set([
          ...existing.aliases,
          ...aliases.filter((alias) => normalizeNameKey(alias) !== normalizeNameKey(existing.name)),
        ])
      );
      const preferName = name.length > existing.name.length ? name : existing.name;
      next = next.map((character) =>
        character.id === existing.id
          ? {
              ...character,
              name: preferName,
              aliases: mergedAliases,
              description: description || character.description,
              role,
              firstAppearanceChapterId:
                character.firstAppearanceChapterId ?? chapterIds[0],
              appearances,
              updatedAt: now,
            }
          : character
      );
      continue;
    }

    next.push({
      id: generateCharacterId(),
      name,
      aliases,
      description,
      role,
      firstAppearanceChapterId: chapterIds[0],
      appearances: chapterIds.map((chapterId) => ({ chapterId })),
      updatedAt: now,
    });
  }

  return sortCharactersMajorFirst(next);
}

/** Derive newline name list for a chapter’s Characters tab (majors first). */
export function chapterCharacterTabText(roster: BookCharacter[], chapterId: string): string {
  const inChapter = roster.filter((c) => c.appearances.some((a) => a.chapterId === chapterId));
  return sortCharactersMajorFirst(inChapter)
    .map((c) => c.name)
    .join("\n");
}

export function syncAllChapterCharacterTabs(
  chapters: Array<{ id: string; tabData: { characters: string } }>,
  roster: BookCharacter[]
): void {
  for (const chapter of chapters) {
    chapter.tabData.characters = chapterCharacterTabText(roster, chapter.id);
  }
}

export function getCharactersFromBookMetadata(metadata: Record<string, unknown> | undefined | null): BookCharacter[] {
  if (!metadata) return [];
  return normalizeCharacterRoster(metadata.characters);
}

export function normalizeCharacterRemoval(raw: unknown): CharacterRemovalRecord | null {
  if (!raw || typeof raw !== "object") return null;
  const rec = raw as Record<string, unknown>;
  const name = typeof rec.name === "string" ? rec.name.trim() : "";
  const reason = typeof rec.reason === "string" ? rec.reason.trim() : "";
  if (!name || !reason) return null;
  const id =
    typeof rec.id === "string" && rec.id.trim() ? rec.id.trim() : generateCharacterRemovalId();
  const aliases = Array.isArray(rec.aliases)
    ? rec.aliases
        .filter((a): a is string => typeof a === "string")
        .map((a) => a.trim())
        .filter(Boolean)
    : [];
  return {
    id,
    characterId: typeof rec.characterId === "string" ? rec.characterId.trim() : undefined,
    name,
    aliases,
    description: typeof rec.description === "string" ? rec.description.trim() : undefined,
    role: rec.role === "major" ? "major" : rec.role === "minor" ? "minor" : undefined,
    reason,
    removedAt:
      typeof rec.removedAt === "string" && rec.removedAt.trim()
        ? rec.removedAt.trim()
        : new Date().toISOString(),
  };
}

export function normalizeCharacterRemovals(raw: unknown): CharacterRemovalRecord[] {
  if (!Array.isArray(raw)) return [];
  const out: CharacterRemovalRecord[] = [];
  for (const item of raw) {
    const record = normalizeCharacterRemoval(item);
    if (record) out.push(record);
  }
  return out.sort((a, b) => b.removedAt.localeCompare(a.removedAt));
}

export function getCharacterRemovalsFromMetadata(
  metadata: Record<string, unknown> | undefined | null
): CharacterRemovalRecord[] {
  if (!metadata) return [];
  return normalizeCharacterRemovals(metadata.characterRemovals);
}

export function characterRemovalNameKeys(record: CharacterRemovalRecord): string[] {
  const keys = new Set<string>();
  const name = normalizeNameKey(record.name);
  if (name) keys.add(name);
  for (const alias of record.aliases) {
    const key = normalizeNameKey(alias);
    if (key) keys.add(key);
  }
  return Array.from(keys);
}

export function isCharacterNameRemoved(
  removals: CharacterRemovalRecord[],
  name: string,
  aliases: string[] = []
): boolean {
  const keys = new Set<string>();
  const primary = normalizeNameKey(name);
  if (primary) keys.add(primary);
  for (const alias of aliases) {
    const key = normalizeNameKey(alias);
    if (key) keys.add(key);
  }
  if (keys.size === 0) return false;
  return removals.some((record) =>
    characterRemovalNameKeys(record).some((removedKey) => keys.has(removedKey))
  );
}

export function createCharacterRemovalRecord(
  character: Pick<BookCharacter, "id" | "name" | "aliases" | "description" | "role">,
  reason: string,
  removedAt = new Date().toISOString()
): CharacterRemovalRecord {
  return {
    id: generateCharacterRemovalId(),
    characterId: character.id,
    name: character.name.trim(),
    aliases: [...character.aliases],
    description: character.description.trim() || undefined,
    role: character.role,
    reason: reason.trim(),
    removedAt,
  };
}

export function withCharacterRemovalsInMetadata(
  metadata: Record<string, unknown>,
  removals: CharacterRemovalRecord[]
): Record<string, unknown> {
  return {
    ...metadata,
    characterRemovals: normalizeCharacterRemovals(removals),
  };
}

export function formatCharacterRemovalsForExtractPrompt(removals: CharacterRemovalRecord[]): string {
  if (!removals.length) return "";
  return removals
    .slice(0, 40)
    .map((record) => {
      const aliases = record.aliases.length ? ` (aka ${record.aliases.join(", ")})` : "";
      return `- "${record.name}"${aliases}: ${record.reason}`;
    })
    .join("\n");
}

export function withCharactersInMetadata(
  metadata: Record<string, unknown>,
  characters: BookCharacter[]
): Record<string, unknown> {
  return {
    ...metadata,
    characters: sortCharactersMajorFirst(characters),
  };
}

/** Seed minor stubs from unique chapter tab name lines when roster is empty. */
export function seedRosterFromChapterTabs(
  chapters: Array<{ id: string; order?: number; tabData?: { characters?: string } }>,
  removals: CharacterRemovalRecord[] = []
): BookCharacter[] {
  const now = new Date().toISOString();
  const byKey = new Map<string, BookCharacter>();

  const ordered = [...chapters].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  for (const chapter of ordered) {
    const lines = String(chapter.tabData?.characters ?? "")
      .split("\n")
      .map((line) => line.trim())
      .filter(Boolean);
    for (const name of lines) {
      if (!isPlausibleCharacterName(name)) continue;
      if (isCharacterNameRemoved(removals, name)) continue;
      const key = normalizeNameKey(name);
      if (!key) continue;
      const existing = byKey.get(key);
      if (existing) {
        if (!existing.appearances.some((a) => a.chapterId === chapter.id)) {
          existing.appearances.push({ chapterId: chapter.id });
        }
      } else {
        byKey.set(key, {
          id: generateCharacterId(),
          name,
          aliases: [],
          description: "",
          role: "minor",
          firstAppearanceChapterId: chapter.id,
          appearances: [{ chapterId: chapter.id }],
          updatedAt: now,
        });
      }
    }
  }
  return sortCharactersMajorFirst(Array.from(byKey.values()));
}

export function appearanceForChapter(
  character: BookCharacter,
  chapterId: string
): CharacterChapterAppearance | undefined {
  return character.appearances.find((a) => a.chapterId === chapterId);
}

export function previousAppearance(
  character: BookCharacter,
  chapterId: string,
  chapterOrder: Array<{ id: string; order: number }>
): CharacterChapterAppearance | undefined {
  const orderMap = new Map(chapterOrder.map((c) => [c.id, c.order]));
  const currentOrder = orderMap.get(chapterId);
  if (currentOrder == null) return undefined;
  const prior = character.appearances
    .map((a) => ({ appearance: a, order: orderMap.get(a.chapterId) }))
    .filter((x): x is { appearance: CharacterChapterAppearance; order: number } => typeof x.order === "number")
    .filter((x) => x.order < currentOrder)
    .sort((a, b) => b.order - a.order);
  return prior[0]?.appearance;
}

export function leverDelta(
  current: CharacterArcLever | undefined,
  previous: CharacterArcLever | undefined
): LeverDelta {
  if (!current) return "flat";
  if (!previous) return "new";
  if (current.score > previous.score) return "up";
  if (current.score < previous.score) return "down";
  return "flat";
}

export function leverDeltaSymbol(delta: LeverDelta): string {
  if (delta === "up") return "↑";
  if (delta === "down") return "↓";
  if (delta === "new") return "·";
  return "—";
}

export function charactersInChapter(roster: BookCharacter[], chapterId: string): BookCharacter[] {
  return sortCharactersMajorFirst(
    roster.filter((c) => c.appearances.some((a) => a.chapterId === chapterId))
  );
}

export function finalLeverScores(character: BookCharacter, chapterOrder: Array<{ id: string; order: number }>): CharacterArcLever[] {
  if (character.role !== "major") return [];
  const orderMap = new Map(chapterOrder.map((c) => [c.id, c.order]));
  const withOrder = character.appearances
    .filter((a) => a.levers && a.levers.length > 0)
    .map((a) => ({ appearance: a, order: orderMap.get(a.chapterId) ?? -1 }))
    .sort((a, b) => a.order - b.order);
  const last = withOrder[withOrder.length - 1]?.appearance;
  return last?.levers ? normalizeLevers(last.levers) : createBlankLevers();
}

/** Series for graphing: one point per chapter appearance that has levers. */
export function leverSeriesForCharacter(
  character: BookCharacter,
  chapterOrder: Array<{ id: string; order: number; title?: string }>
): Array<{
  chapterId: string;
  chapterTitle: string;
  order: number;
  levers: CharacterArcLever[];
}> {
  if (character.role !== "major") return [];
  const orderMap = new Map(chapterOrder.map((c) => [c.id, c]));
  return character.appearances
    .filter((a) => a.levers && a.levers.length > 0)
    .map((a) => {
      const meta = orderMap.get(a.chapterId);
      return {
        chapterId: a.chapterId,
        chapterTitle: meta?.title ?? "Chapter",
        order: meta?.order ?? 0,
        levers: normalizeLevers(a.levers),
      };
    })
    .sort((a, b) => a.order - b.order);
}

export function isStructuredCharacterRecord(item: unknown): boolean {
  if (!item || typeof item !== "object") return false;
  const rec = item as Record<string, unknown>;
  return (
    typeof rec.name === "string" &&
    (Array.isArray(rec.appearances) ||
      Array.isArray(rec.aliases) ||
      typeof rec.description === "string" ||
      rec.role === "major" ||
      rec.role === "minor" ||
      typeof rec.id === "string")
  );
}

/** Prefer full roster JSON; fall back to name-only rollup / legacy. */
export function rosterFromExtractedJson(extracted: unknown): BookCharacter[] {
  if (!Array.isArray(extracted) || extracted.length === 0) return [];
  const structured = extracted.every((item) => isStructuredCharacterRecord(item));
  if (structured) {
    const roster = normalizeCharacterRoster(extracted);
    if (roster.some((c) => c.appearances.length > 0 || c.description || c.aliases.length > 0 || c.role === "major")) {
      return roster;
    }
  }
  // Name-only: treat as empty roster (seed from chapter tabs instead)
  return [];
}
